import React, { useMemo, useState, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  Button,
  Chip,
  Alert,
  Skeleton,
  TextField,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import FilterListIcon from '@mui/icons-material/FilterList'
import { useEntityFilterSummary, type EntityMetricBounds, type LabelValue } from '../../hooks/useEntityFilterSummary'
import { NumericRangeFilter } from './NumericRangeFilter'
import { isNumericLabelValue, type RangeFilter, type DateRange } from './types'
import { BRAND_GRADIENT, SURFACE_COLORS } from './design-tokens'

// Filter category definitions
type FilterCategory = 'campaign' | 'analysis' | 'video'

/** One rendered sidebar control: an entity label (checkboxes) or a metric (slider). */
type FilterItem =
  | { kind: 'label'; key: string }
  | { kind: 'metric'; key: string; bounds: EntityMetricBounds }

interface FilterCategoryConfig {
  id: FilterCategory
  title: string
  subtitle: string
}

const FILTER_CATEGORIES: FilterCategoryConfig[] = [
  {
    id: 'campaign',
    title: 'Campaign Metrics',
    subtitle: 'Campaign and content selection',
  },
  {
    id: 'analysis',
    title: 'Analysis Metrics',
    subtitle: 'Calculated engagement scores',
  },
  {
    id: 'video',
    title: 'Post Metrics',
    subtitle: 'Raw post performance',
  },
]

// Map label keys to categories (lowercase for matching)
const LABEL_CATEGORY_MAP: Record<string, FilterCategory> = {
  // Campaign Metrics
  campaign: 'campaign',
  boost: 'campaign',
  boosted: 'campaign',
  hashtag: 'campaign',
  creator: 'campaign',
  account: 'campaign',
  // Analysis Metrics
  engagement_rate: 'analysis',
  engagement_rate_pct: 'analysis',
  meaningful_er_pct: 'analysis',
  meaningful_engagement_score: 'analysis',
  u35_pct: 'analysis',
  engagement_score: 'analysis',
  meaningful_er: 'analysis',
  u35: 'analysis',
  // Video Metrics (default for numeric metrics)
  likes: 'video',
  comments: 'video',
  shares: 'video',
  views: 'video',
  reach: 'video',
  saves: 'video',
}

// Format label key for display: remove underscores and capitalize each word
const formatLabelKey = (key: string): string => {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

// Get category for a label key
const getLabelCategory = (key: string, isNumeric: boolean): FilterCategory => {
  const lowerKey = key.toLowerCase()

  // Check exact match first
  if (LABEL_CATEGORY_MAP[lowerKey]) {
    return LABEL_CATEGORY_MAP[lowerKey]
  }

  // Check partial matches
  for (const [mapKey, category] of Object.entries(LABEL_CATEGORY_MAP)) {
    if (lowerKey.includes(mapKey) || mapKey.includes(lowerKey)) {
      return category
    }
  }

  // Default: numeric labels go to video, text labels go to campaign
  return isNumeric ? 'video' : 'campaign'
}

export interface LabelFilterBuilderProps {
  customer: string
  job: string
  /**
   * A Sky product slug. When given, the label universe, the metric bounds and
   * the campaign facet come from the product-scoped mount rather than the whole
   * job — so a department's sliders span ITS videos, not everyone's (T2.3).
   */
  product?: string
  /** Text filter selections (exact match) */
  selectedLabels: Record<string, string[]>
  /** Called when text filters change */
  onChange: (selectedLabels: Record<string, string[]>) => void
  /** Range filter selections over entity metrics, keyed by metric key */
  rangeFilters?: Record<string, RangeFilter>
  /** Called when metric range filters change */
  onRangeFiltersChange?: (rangeFilters: Record<string, RangeFilter>) => void
  /** Selected platforms (entity-level). Empty = no constraint. */
  platforms?: string[]
  /** Called when the platform selection changes */
  onPlatformsChange?: (platforms: string[]) => void
  /** Published date range filter (video publish date) */
  publishedDateRange?: DateRange | null
  /** Called when published date range changes */
  onPublishedDateRangeChange?: (dateRange: DateRange | null) => void
  /** Comment date range filter (comment creation date) */
  commentDateRange?: DateRange | null
  /** Called when comment date range changes */
  onCommentDateRangeChange?: (dateRange: DateRange | null) => void
  disabled?: boolean
  /**
   * The entity LABEL keys (from `useEntityFilterSummary().labels`) this mount
   * offers as filters, in the same semantics as `MetadataFilterSection`'s
   * `allowKeys`: omitted DISCOVERS every label key the mount has — right for a
   * job-level/admin surface exploring unfamiliar data. Given, the result is the
   * intersection with the keys this mount actually has — a curated product
   * dashboard passes `SKY_SIDEBAR_LABEL_KEYS` (`platformFilter.ts`), and an
   * empty array is the decision "this mount offers no label facets", not an
   * oversight.
   */
  labelKeys?: readonly string[]
}

/**
 * DateRangeSection — auto-applies date filter on change with 300ms debounce.
 * Local state gives immediate input feedback; debounce prevents excessive API calls.
 * HTML5 date inputs fire change events with complete YYYY-MM-DD values (not per-keystroke).
 */
const DateRangeSection: React.FC<{
  publishedDateRange?: DateRange | null
  onPublishedDateRangeChange?: (dateRange: DateRange | null) => void
  commentDateRange?: DateRange | null
  onCommentDateRangeChange?: (dateRange: DateRange | null) => void
  disabled?: boolean
}> = ({ publishedDateRange, onPublishedDateRangeChange, commentDateRange, onCommentDateRangeChange, disabled }) => {
  const [pubFrom, setPubFrom] = useState(publishedDateRange?.from || '')
  const [pubTo, setPubTo] = useState(publishedDateRange?.to || '')
  const [comFrom, setComFrom] = useState(commentDateRange?.from || '')
  const [comTo, setComTo] = useState(commentDateRange?.to || '')

  // Sync local state when parent clears filters
  useEffect(() => {
    setPubFrom(publishedDateRange?.from || '')
    setPubTo(publishedDateRange?.to || '')
  }, [publishedDateRange?.from, publishedDateRange?.to])

  useEffect(() => {
    setComFrom(commentDateRange?.from || '')
    setComTo(commentDateRange?.to || '')
  }, [commentDateRange?.from, commentDateRange?.to])

  // Apply filters on blur — lets user finish typing the full date before filtering
  const applyPublished = useCallback(
    (from: string, to: string) => {
      if (!from && !to) {
        onPublishedDateRangeChange?.(null)
      } else {
        onPublishedDateRangeChange?.({ from: from || undefined, to: to || undefined })
      }
    },
    [onPublishedDateRangeChange],
  )

  const applyComment = useCallback(
    (from: string, to: string) => {
      if (!from && !to) {
        onCommentDateRangeChange?.(null)
      } else {
        onCommentDateRangeChange?.({ from: from || undefined, to: to || undefined })
      }
    },
    [onCommentDateRangeChange],
  )

  const dateFieldSx = {
    '& .MuiOutlinedInput-root': {
      bgcolor: 'white',
      '& fieldset': { borderColor: 'grey.200' },
    },
  }

  return (
    <>
      {onPublishedDateRangeChange && (
        <>
          <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'grey.100' }}>
            <Typography
              variant="caption"
              sx={{ color: 'grey.700', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em', fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <CalendarTodayIcon sx={{ fontSize: 12 }} />
              Published Date
            </Typography>
          </Box>
          <Box sx={{ px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5, borderBottom: '1px solid', borderColor: 'grey.100' }}>
            <TextField label="From" type="date" size="small" value={pubFrom} onChange={(e) => setPubFrom(e.target.value)} disabled={disabled} InputLabelProps={{ shrink: true }} sx={dateFieldSx} />
            <TextField label="To" type="date" size="small" value={pubTo} onChange={(e) => setPubTo(e.target.value)} disabled={disabled} InputLabelProps={{ shrink: true }} sx={dateFieldSx} />
            <Button
              size="small"
              variant="contained"
              onClick={() => applyPublished(pubFrom, pubTo)}
              disabled={disabled || (pubFrom === (publishedDateRange?.from || '') && pubTo === (publishedDateRange?.to || ''))}
              sx={{
                textTransform: 'none',
                fontSize: '0.8rem',
                fontWeight: 600,
                bgcolor: '#667eea',
                '&:hover': { bgcolor: '#5a6fd6' },
                '&.Mui-disabled': { bgcolor: 'grey.200', color: 'grey.500' },
              }}
            >
              Apply
            </Button>
          </Box>
        </>
      )}

      {onCommentDateRangeChange && (
        <>
          <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'grey.100' }}>
            <Typography
              variant="caption"
              sx={{ color: 'grey.700', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em', fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <CalendarTodayIcon sx={{ fontSize: 12 }} />
              Comment Date
            </Typography>
          </Box>
          <Box sx={{ px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5, borderBottom: '1px solid', borderColor: 'grey.100' }}>
            <TextField label="From" type="date" size="small" value={comFrom} onChange={(e) => setComFrom(e.target.value)} disabled={disabled} InputLabelProps={{ shrink: true }} sx={dateFieldSx} />
            <TextField label="To" type="date" size="small" value={comTo} onChange={(e) => setComTo(e.target.value)} disabled={disabled} InputLabelProps={{ shrink: true }} sx={dateFieldSx} />
            <Button
              size="small"
              variant="contained"
              onClick={() => applyComment(comFrom, comTo)}
              disabled={disabled || (comFrom === (commentDateRange?.from || '') && comTo === (commentDateRange?.to || ''))}
              sx={{
                textTransform: 'none',
                fontSize: '0.8rem',
                fontWeight: 600,
                bgcolor: '#667eea',
                '&:hover': { bgcolor: '#5a6fd6' },
                '&.Mui-disabled': { bgcolor: 'grey.200', color: 'grey.500' },
              }}
            >
              Apply
            </Button>
          </Box>
        </>
      )}
    </>
  )
}

/**
 * LabelFilterBuilder Component
 *
 * Displays grouped pipeline labels with checkboxes for building compound queries.
 * Users can select multiple values per label key.
 * Features refined accordion styling with modern visual treatment.
 */
export const LabelFilterBuilder: React.FC<LabelFilterBuilderProps> = ({
  customer,
  job,
  product,
  selectedLabels,
  onChange,
  rangeFilters = {},
  onRangeFiltersChange,
  platforms = [],
  onPlatformsChange,
  publishedDateRange,
  onPublishedDateRangeChange,
  commentDateRange,
  onCommentDateRangeChange,
  disabled = false,
  labelKeys,
}) => {
  const {
    labels,
    platforms: availablePlatforms,
    metrics,
    totalEntities,
    isLoading,
    error,
  } = useEntityFilterSummary({ customer, job, product })

  // Categorize entity labels (checkbox filters) and entity metrics (sliders)
  const categorizedItems = useMemo(() => {
    const result: Record<FilterCategory, FilterItem[]> = {
      campaign: [],
      analysis: [],
      video: [],
    }

    // Labels handled by dedicated UI (date picker) — exclude from generic filters
    const DATE_LABEL_KEYS = ['published_date', 'publish_date', 'date', 'created_date']

    // `undefined` DISCOVERS every key; a given list (possibly empty) restricts
    // to the intersection — see the `labelKeys` prop doc.
    const allowedLabelKeys = labelKeys ? new Set(labelKeys) : undefined

    Object.entries(labels ?? {}).forEach(([key, values]) => {
      if (DATE_LABEL_KEYS.includes(key.toLowerCase())) return
      if (allowedLabelKeys && !allowedLabelKeys.has(key)) return
      const isNumeric = values.every((lv) => isNumericLabelValue(lv.value)) && values.length > 0
      result[getLabelCategory(key, isNumeric)].push({ kind: 'label', key })
    })

    metrics.forEach((bounds) => {
      result[getLabelCategory(bounds.metric, true)].push({ kind: 'metric', key: bounds.metric, bounds })
    })

    // Sort each category alphabetically
    for (const cat of Object.keys(result) as FilterCategory[]) {
      result[cat].sort((a, b) => a.key.localeCompare(b.key))
    }

    return result
  }, [labels, metrics, labelKeys])

  const hasAnyFilter =
    Object.values(categorizedItems).some((items) => items.length > 0) || availablePlatforms.length > 0

  // Handle toggling a single value
  const handleValueToggle = (key: string, value: string) => {
    const current = selectedLabels[key] || []
    const isSelected = current.includes(value)

    let newValues: string[]
    if (isSelected) {
      newValues = current.filter((v) => v !== value)
    } else {
      newValues = [...current, value]
    }

    const newSelection = { ...selectedLabels }
    if (newValues.length === 0) {
      delete newSelection[key]
    } else {
      newSelection[key] = newValues
    }

    onChange(newSelection)
  }

  // Handle "Select All" for a label key
  const handleSelectAll = (key: string) => {
    if (!labels?.[key]) return
    const allValues = labels[key].map((lv) => lv.value)
    onChange({
      ...selectedLabels,
      [key]: allValues,
    })
  }

  // Handle "Clear" for a label key
  const handleClear = (key: string) => {
    const newSelection = { ...selectedLabels }
    delete newSelection[key]
    onChange(newSelection)
  }

  // Count selected values for a key
  const getSelectedCount = (key: string): number => {
    return selectedLabels[key]?.length || 0
  }

  // Check if a value is selected
  const isValueSelected = (key: string, value: string): boolean => {
    return selectedLabels[key]?.includes(value) || false
  }

  // Handle toggling a platform
  const handlePlatformToggle = (platform: string) => {
    if (!onPlatformsChange) return
    onPlatformsChange(
      platforms.includes(platform)
        ? platforms.filter((p) => p !== platform)
        : [...platforms, platform],
    )
  }

  // Handle range filter change
  const handleRangeFilterChange = (key: string, value: RangeFilter | undefined) => {
    if (!onRangeFiltersChange) return

    const newFilters = { ...rangeFilters }
    if (value === undefined) {
      delete newFilters[key]
    } else {
      newFilters[key] = value
    }
    onRangeFiltersChange(newFilters)
  }

  if (isLoading) {
    return (
      <Box>
        {/* Header skeleton */}
        <Box
          sx={{
            p: 2,
            background: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',
          }}
        >
          <Skeleton variant="text" width={80} height={24} sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
          <Skeleton variant="text" width={120} height={16} sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
        </Box>
        <Box sx={{ p: 2 }}>
          <Skeleton variant="rectangular" height={48} sx={{ mb: 1, borderRadius: 1 }} />
          <Skeleton variant="rectangular" height={48} sx={{ mb: 1, borderRadius: 1 }} />
          <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 1 }} />
        </Box>
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load labels: {error.message}
      </Alert>
    )
  }

  if (!hasAnyFilter) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No filters found for posts in this job.
      </Alert>
    )
  }

  // Render a text filter accordion
  const renderTextFilter = (key: string) => {
    const values = labels?.[key] ?? []
    const selectedCount = getSelectedCount(key)
    const allSelected = selectedCount === values.length && values.length > 0

    return (
      <Accordion
        key={key}
        disableGutters
        elevation={0}
        sx={{
          '&:before': { display: 'none' },
          '&.Mui-expanded': {
            bgcolor: SURFACE_COLORS.filterAccordionExpanded,
          },
          borderBottom: '1px solid',
          borderColor: 'grey.100',
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: 'grey.500' }} />}
          sx={{
            '& .MuiAccordionSummary-content': {
              alignItems: 'center',
              gap: 1.5,
            },
            transition: 'background 0.15s ease',
            '&:hover': {
              bgcolor: SURFACE_COLORS.filterAccordionHover,
            },
          }}
        >
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '0.875rem',
              color: 'grey.800',
            }}
          >
            {formatLabelKey(key)}
          </Typography>
          {selectedCount > 0 && (
            <Chip
              label={selectedCount}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.7rem',
                fontWeight: 600,
                background: BRAND_GRADIENT,
                color: 'white',
                '& .MuiChip-label': { px: 1 },
              }}
            />
          )}
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0, pb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              mb: 1.5,
              pb: 1.5,
              borderBottom: 1,
              borderColor: 'grey.100',
            }}
          >
            <Button
              size="small"
              variant="text"
              onClick={() => handleSelectAll(key)}
              disabled={disabled || allSelected}
              sx={{
                fontSize: '0.75rem',
                textTransform: 'none',
                color: 'grey.600',
                '&:hover': { bgcolor: 'grey.100' },
              }}
            >
              Select All
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={() => handleClear(key)}
              disabled={disabled || selectedCount === 0}
              sx={{
                fontSize: '0.75rem',
                textTransform: 'none',
                color: 'grey.600',
                '&:hover': { bgcolor: 'grey.100' },
              }}
            >
              Clear
            </Button>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {values.map((labelValue: LabelValue) => (
              <FormControlLabel
                key={labelValue.value}
                control={
                  <Checkbox
                    checked={isValueSelected(key, labelValue.value)}
                    onChange={() => handleValueToggle(key, labelValue.value)}
                    disabled={disabled}
                    size="small"
                    sx={{
                      color: 'grey.400',
                      '&.Mui-checked': {
                        color: '#667eea',
                      },
                    }}
                  />
                }
                label={
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'grey.700',
                        fontSize: '0.8125rem',
                      }}
                    >
                      {labelValue.value}
                    </Typography>
                    <Chip
                      label={labelValue.count}
                      size="small"
                      variant="outlined"
                      sx={{
                        height: 18,
                        fontSize: '0.65rem',
                        borderColor: 'grey.200',
                        color: 'grey.500',
                        '& .MuiChip-label': { px: 0.75 },
                      }}
                    />
                  </Box>
                }
                sx={{
                  m: 0,
                  py: 0.25,
                  borderRadius: 1,
                  transition: 'background 0.1s',
                  '&:hover': {
                    bgcolor: 'rgba(0,0,0,0.02)',
                  },
                }}
              />
            ))}
          </Box>
        </AccordionDetails>
      </Accordion>
    )
  }

  // Render a metric slider — bounds come from the entity metrics table
  // (real MIN/MAX), not from regexp-parsing label strings.
  const renderMetricFilter = (bounds: EntityMetricBounds) => (
    <NumericRangeFilter
      key={bounds.metric}
      labelKey={bounds.metric}
      displayName={bounds.displayName || formatLabelKey(bounds.metric)}
      value={rangeFilters[bounds.metric]}
      onChange={(value) => handleRangeFilterChange(bounds.metric, value)}
      bounds={{ min: bounds.min, max: bounds.max }}
      isPercent={bounds.format === 'percent'}
      disabled={disabled}
    />
  )

  // Render the platform checkbox group
  const renderPlatformSection = () => (
    <>
      <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'grey.100' }}>
        <Typography
          variant="caption"
          sx={{ color: 'grey.700', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em', fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 0.5 }}
        >
          <FilterListIcon sx={{ fontSize: 12 }} />
          Platform
        </Typography>
      </Box>
      <Box sx={{ px: 2, py: 1, display: 'flex', flexDirection: 'column', gap: 0.25, borderBottom: '1px solid', borderColor: 'grey.100' }}>
        {availablePlatforms.map((platform) => (
          <FormControlLabel
            key={platform.value}
            control={
              <Checkbox
                checked={platforms.includes(platform.value)}
                onChange={() => handlePlatformToggle(platform.value)}
                disabled={disabled}
                size="small"
                sx={{ color: 'grey.400', '&.Mui-checked': { color: '#667eea' } }}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ color: 'grey.700', fontSize: '0.8125rem', textTransform: 'capitalize' }}>
                  {platform.value}
                </Typography>
                <Chip
                  label={platform.count}
                  size="small"
                  variant="outlined"
                  sx={{ height: 18, fontSize: '0.65rem', borderColor: 'grey.200', color: 'grey.500', '& .MuiChip-label': { px: 0.75 } }}
                />
              </Box>
            }
            sx={{ m: 0, py: 0.25, borderRadius: 1 }}
          />
        ))}
      </Box>
    </>
  )

  // Render category section header
  const renderCategoryHeader = (config: FilterCategoryConfig) => (
    <Box
      key={`header-${config.id}`}
      sx={{
        px: 2,
        py: 1.5,
        bgcolor: 'background.default',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: 'grey.700',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.03em',
          fontSize: '0.7rem',
          display: 'block',
        }}
      >
        {config.title}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: 'grey.600',
          fontSize: '0.8rem',
        }}
      >
        {config.subtitle}
      </Typography>
    </Box>
  )

  return (
    <Box>
      {/* Filter summary */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {totalEntities} post{totalEntities !== 1 ? 's' : ''} available
        </Typography>
      </Box>

      {/* Date Range Filters */}
      {(onPublishedDateRangeChange || onCommentDateRangeChange) && (
        <DateRangeSection
          publishedDateRange={publishedDateRange}
          onPublishedDateRangeChange={onPublishedDateRangeChange}
          commentDateRange={commentDateRange}
          onCommentDateRangeChange={onCommentDateRangeChange}
          disabled={disabled}
        />
      )}

      {/* Platform filter (entity-level) */}
      {onPlatformsChange && availablePlatforms.length > 0 && renderPlatformSection()}

      {/* Render filters grouped by category */}
      {FILTER_CATEGORIES.map((categoryConfig) => {
        const categoryItems = categorizedItems[categoryConfig.id].filter(
          (item) => item.kind === 'label' || Boolean(onRangeFiltersChange),
        )
        if (categoryItems.length === 0) return null

        return (
          <React.Fragment key={categoryConfig.id}>
            {renderCategoryHeader(categoryConfig)}
            {categoryItems.map((item) =>
              item.kind === 'metric' ? renderMetricFilter(item.bounds) : renderTextFilter(item.key),
            )}
          </React.Fragment>
        )
      })}
    </Box>
  )
}

export default LabelFilterBuilder
